import { Send } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';

import styles from './ChatInput.module.css';

interface ChatInputProps {
  avatar?: ReactNode;
  disabled?: boolean;
  onTypingChange?: (isTyping: boolean) => void;
  onSend: (message: string) => Promise<void>;
}

export function ChatInput({
  avatar,
  disabled = false,
  onSend,
  onTypingChange,
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const isTyping = !disabled && value.trim().length > 0;

  useEffect(() => {
    onTypingChange?.(isTyping);
  }, [isTyping, onTypingChange]);

  useEffect(() => {
    return () => onTypingChange?.(false);
  }, [onTypingChange]);

  async function submit() {
    const trimmed = value.trim();

    if (!trimmed || disabled) {
      return;
    }

    setValue('');
    onTypingChange?.(false);
    await onSend(trimmed);
  }

  return (
    <div className={styles.inputRow}>
      <div className={styles.controls}>
        <textarea
          aria-label="Message to AI study assistant"
          className={styles.textarea}
          disabled={disabled}
          id="ai-study-assistant-input"
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
          placeholder="Type your message..."
          rows={1}
          value={value}
        />
        <Button
          aria-label="Send message"
          disabled={disabled || value.trim().length === 0}
          iconOnly
          onClick={() => {
            void submit();
          }}
          variant="primary"
        >
          <Send aria-hidden="true" size={18} />
        </Button>
        {avatar ? <div className={styles.avatarSlot}>{avatar}</div> : null}
      </div>
    </div>
  );
}
